// Personae library — list page.
//
// Content-editor + super_admin surface for browsing test archetypes used
// by the AI Coach quality-loop testbed (GC Labs). Mirrors the shape of
// FrameworksLibraryPage + TenantsPage — PageHeader / stat cards / filter
// chips / table.
//
// Per design/UI : UX v1.0/for-engineering-personae/specs/personae-strings.md
// § View 1. Status filter defaults to 'active' (archived hidden).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, StatCard } from '@gc-coach/design-system';
import type { PersonaStatus, PersonaSummary } from '@gc-coach/shared';
import { AdminShell, PageHeader } from '../shell/index.js';
import { useI18n } from '../i18n/index.js';
import { listPersonae } from '../api/endpoints.js';

type StatusFilter = PersonaStatus | 'all';

export function PersonaeListPage() {
  const { t } = useI18n();
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [q, setQ] = useState('');
  const [personae, setPersonae] = useState<PersonaSummary[]>([]);
  const [counts, setCounts] = useState({
    total: 0,
    active: 0,
    archived: 0,
    scenarios_run: 0,
    responses_reviewed: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const result = await listPersonae({ status: statusFilter, q });
      if (cancelled) return;
      if (result.ok) {
        setPersonae([...result.data.personae]);
        setCounts(result.data.counts);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [statusFilter, q]);

  const subText = useMemo(
    () => `${counts.active} active · ${counts.archived} archived`,
    [counts],
  );

  return (
    <AdminShell
      activeKey="personae"
      breadcrumb={[t('personae.breadcrumbKnowledge'), t('personae.breadcrumbPersonae')]}
      state={isLoading && personae.length === 0 ? { kind: 'loading' } : { kind: 'normal' }}
    >
      <PageHeader
        title={t('personae.listTitle')}
        sub={subText}
        actions={
          <Button
            kind="primary"
            onClick={() => navigate('/admin/knowledge/personae/new')}
          >
            {t('personae.newPersonaButton')}
          </Button>
        }
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <StatCard label={t('personae.statTotal')} value={counts.total.toString()} />
        <StatCard label={t('personae.statActive')} value={counts.active.toString()} />
        <StatCard
          label={t('personae.statScenariosRun')}
          value={counts.scenarios_run.toString()}
        />
        <StatCard
          label={t('personae.statResponsesReviewed')}
          value={counts.responses_reviewed.toString()}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        {(['all', 'active', 'archived'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            style={{
              padding: '4px 12px',
              borderRadius: 999,
              border: '1px solid var(--border)',
              background: statusFilter === s ? 'var(--brand)' : 'transparent',
              color: statusFilter === s ? '#fff' : 'var(--ink1)',
              cursor: 'pointer',
              fontSize: 12,
              fontFamily: 'var(--font-mono)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {t(`personae.filter${s.charAt(0).toUpperCase()}${s.slice(1)}` as const)}
          </button>
        ))}
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('personae.searchPlaceholder')}
          style={{
            marginLeft: 'auto',
            padding: '6px 10px',
            border: '1px solid var(--border)',
            borderRadius: 4,
            fontSize: 13,
            minWidth: 240,
          }}
        />
      </div>

      {personae.length === 0 && !isLoading ? (
        <div
          style={{
            padding: 40,
            textAlign: 'center',
            color: 'var(--ink3)',
            background: 'var(--paper)',
            border: '1px solid var(--border)',
            borderRadius: 6,
          }}
        >
          <h2 style={{ margin: 0, marginBottom: 8, color: 'var(--ink1)' }}>
            {t('personae.emptyTitle')}
          </h2>
          <p style={{ margin: 0, marginBottom: 16 }}>{t('personae.emptyBody')}</p>
          <Button
            kind="primary"
            onClick={() => navigate('/admin/knowledge/personae/new')}
          >
            {t('personae.newPersonaButton')}
          </Button>
        </div>
      ) : (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--paper)', textAlign: 'left' }}>
                <th style={thStyle}>{t('personae.colName')}</th>
                <th style={thStyle}>{t('personae.colSector')}</th>
                <th style={thStyle}>{t('personae.colSize')}</th>
                <th style={thStyle}>{t('personae.colJurisdiction')}</th>
                <th style={thStyle}>{t('personae.colScenarios')}</th>
                <th style={thStyle}>{t('personae.colUpdated')}</th>
              </tr>
            </thead>
            <tbody>
              {personae.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => navigate(`/admin/knowledge/personae/${p.id}`)}
                  style={{ cursor: 'pointer', borderTop: '1px solid var(--border)' }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = 'var(--paper)')
                  }
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={tdStyle}>
                    <strong>{p.name}</strong>
                    {p.status === 'archived' && (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 10,
                          padding: '2px 6px',
                          background: 'var(--paper)',
                          border: '1px solid var(--border)',
                          borderRadius: 3,
                          color: 'var(--ink3)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                        }}
                      >
                        {t('personae.archivedBadge')}
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}>{p.sector ?? '—'}</td>
                  <td style={tdStyle}>{p.revenue_band ?? '—'}</td>
                  <td style={tdStyle}>{p.primary_jurisdiction ?? '—'}</td>
                  <td style={tdStyle}>{p.scenarios_count}</td>
                  <td style={tdStyle}>
                    {new Date(p.updated_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
}

const thStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 11,
  fontFamily: 'var(--font-mono)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--ink3)',
  fontWeight: 600,
};

const tdStyle: React.CSSProperties = {
  padding: '12px',
  verticalAlign: 'middle',
};
